import { IS_BILLING, MODULES, USER_TYPE } from "@/constants";
import { IPermissions, IUser } from "@/modules/User/user.interface";
import { hasPaymentIssue, isUserBillingAdmin } from "@/utils/user";
import * as React from "react";
import { Navigate } from "react-router-dom";
// Aliased: `routes` is the local route-list variable inside permittedRoutes.
import appRoutes from "@/routes";
import AccountInfoRoutes from "./dashboard/accountInfo.routes";
import AdvertiserRoutes from "./dashboard/advertiser.routes";
import AssetRoutes from "./dashboard/asset.routes";
import AudienceRoutes from "./dashboard/audience.routes";
import BillingRoutes from "./dashboard/billing.route";
import CampaignRoutes from "./dashboard/campaign.routes";
import ReportsCampaignSummaryRoutes from "./dashboard/campaignSummaryReports.routes";
import { downloadsRouteMode } from "./downloadsRouteMode";
import ContactRoutes from "./dashboard/contact.routes";
import ConversionRoutes from "./dashboard/conversion.routes";
import CustomListRoutes from "./dashboard/customList.routes";
import EmailOutreachRoutes from "./dashboard/emailOutreach.routes";
import HomeRoutes from "./dashboard/home.routes";
import VisitorInsightRoutes from "./dashboard/insight.routes";
import IntegrationRoutes from "./dashboard/integration.routes";
import LeadRoutes from "./dashboard/leadGen.routes";
import LineItemsRoutes from "./dashboard/lineItems.routes";
import LinkedInEngagementRoutes from "./dashboard/linkedInEngagement.routes";
import NativeOfferRoutes from "./dashboard/nativeOffers.routes";
import PresetRoutes from "./dashboard/preset.routes";
import ReportRoutes from "./dashboard/reports.routes";
import ScheduledReportsRoutes from "./dashboard/scheduledReports.routes";
import { default as SetupRoutes } from "./dashboard/setup.routes";
import PixelRoutes from "./dashboard/siteVisiter.routes";
import TemplateRoutes from "./dashboard/template.routes";
import UserRoutes from "./dashboard/user.routes";
import UtmRoutes from "./dashboard/utm.routes";

const MainLayout = React.lazy(() => import("@/layouts/MainLayout"));

interface MainRoutesParams {
  user: IUser;
  doesPreviousUserExists?: boolean;
}

export const MainRoutes = ({
  user,
  doesPreviousUserExists = false,
}: MainRoutesParams) => {
  const permission = user?.Role?.$permissions;
  if (!user) return [];
  return [
    {
      element: <MainLayout />,
      path: "dashboard",
      children: permittedRoutes({
        permission,
        isAdmin: user?.Role?.name === USER_TYPE.SUPER_ADMIN,
        user,
        doesPreviousUserExists,
      }),
    },
  ];
};

interface PermittedRoutesParams {
  permission: IPermissions;
  isAdmin: boolean;
  user: IUser;
  doesPreviousUserExists?: boolean;
}

function permittedRoutes({
  permission,
  isAdmin,
  user,
  doesPreviousUserExists = false,
}: PermittedRoutesParams) {
  // If user has customer but no subscription, only show billing and account info routes
  const routes = [];
  const isBillingAdmin = isUserBillingAdmin(user, doesPreviousUserExists);

  if (IS_BILLING && user?.customer?.id) {
    // If user sync failed, only show billing routes (for sync status page)
    if (user?.customer?.stripeSyncStatus === "failed") {
      return [BillingRoutes];
    }
    // If user is syncing or has no subscription (but not past_due), show limited routes
    // Users with past_due status should have full access with payment warnings
    if (
      !user?.customer?.subscription ||
      user?.customer?.stripeSyncStatus === "pending"
    ) {
      // Check if this is truly no subscription or just a past_due situation
      // past_due users have a subscription, just with payment issues
      if (user?.customer?.subscription?.status !== "past_due") {
        return [AccountInfoRoutes, BillingRoutes];
      }
    }

    // Handle payment issues
    if (hasPaymentIssue(user)) {
      if (isBillingAdmin) {
        // Billing admin with payment issue: Account Info + Billing routes only
        return [AccountInfoRoutes, BillingRoutes];
      }
      // Non-admin with payment issue: PrivateRoute will block them entirely
      // Just return minimal routes as fallback
      return [AccountInfoRoutes];
    }

    // Only billing admin gets access to billing routes
    if (isBillingAdmin) {
      routes.push(BillingRoutes);
    }
  }

  if (isAdmin) return ADMIN_ROUTES;

  if (permission[MODULES.DASHBOARD]) {
    routes.push(HomeRoutes);
  }
  routes.push(AccountInfoRoutes);
  // Every user's own report emails. Not in ADMIN_ROUTES: super admin accounts
  // live outside the users table and the API refuses to schedule for them.
  routes.push(ScheduledReportsRoutes);
  // LEGACY (2026-10-14): `/dashboard/downloads` belongs to the old ABM page.
  // Needs ABM_STATS on every brand; see downloadsRouteMode.
  const downloadsMode = downloadsRouteMode(permission);
  if (downloadsMode === "page") {
    routes.push(ReportsCampaignSummaryRoutes);
  } else if (downloadsMode === "redirect") {
    routes.push({
      path: ReportsCampaignSummaryRoutes.path,
      element: (
        <Navigate
          replace
          to={`${appRoutes.DASHBOARD}/${appRoutes.REPORTS.INDEX}/${appRoutes.REPORTS.ABM_BETA.INDEX}`}
        />
      ),
    });
  }
  if (
    permission[MODULES.INTEGRATIONS_BEESWAX] ||
    permission[MODULES.INTEGRATIONS_LINKEDIN] ||
    permission[MODULES.INTEGRATIONS_FACEBOOK] ||
    permission[MODULES.INTEGRATIONS_HEYREACH] ||
    permission[MODULES.INTEGRATIONS_SMARTLEAD] ||
    permission[MODULES.INTEGRATIONS_DISPLAYVIDEO360] ||
    permission[MODULES.INTEGRATIONS_TRADEDESK] ||
    permission[MODULES.INTEGRATIONS_LIVERAMP] ||
    permission[MODULES.INTEGRATIONS_SALESFORCE] ||
    permission[MODULES.INTEGRATIONS_HUBSPOT] ||
    permission[MODULES.INTEGRATIONS_CLAY]
  ) {
    routes.push(IntegrationRoutes);
  }
  if (permission[MODULES.ADVERTISERS]) {
    routes.push(AdvertiserRoutes);
  }
  if (permission[MODULES.CAMPAIGNS] || permission[MODULES.QUICK_CAMPAIGNS]) {
    routes.push(CampaignRoutes(permission, isAdmin));
  }
  // CAMPAIGNS only (not QUICK_CAMPAIGNS): row-click opens the campaign-nested
  // display edit route, which is registered only under CAMPAIGNS.
  if (permission[MODULES.CAMPAIGNS]) {
    routes.push(LineItemsRoutes);
  }
  if (
    permission[MODULES.AUDIENCE_ACCOUNT] ||
    permission[MODULES.AUDIENCE_EMAIL] ||
    permission[MODULES.AUDIENCE_SITE_VISITOR] ||
    permission[MODULES.AUDIENCE_MANAGER]
  ) {
    routes.push(AudienceRoutes(permission, isAdmin));
  }
  if (permission[MODULES.CREATIVES]) {
    routes.push(AssetRoutes);
  }
  if (permission[MODULES.NATIVE_OFFERS]) {
    routes.push(NativeOfferRoutes);
  }
  if (permission[MODULES.CREATIVE_TEMPLATES]) {
    routes.push(TemplateRoutes);
  }
  if (permission[MODULES.FORMS]) {
    routes.push(LeadRoutes);
  }
  if (permission[MODULES.UTMS]) {
    routes.push(UtmRoutes);
  }
  if (permission[MODULES.TAGS]) {
    routes.push(PixelRoutes);
  }
  if (permission[MODULES.CONVERSIONS]) {
    routes.push(ConversionRoutes);
  }
  if (
    permission[MODULES.COMPANY_VISITOR_INSIGHTS] ||
    permission[MODULES.PROFILE_VISITOR_INSIGHTS]
  ) {
    routes.push(VisitorInsightRoutes(permission));
  }
  if (permission[MODULES.CONTACT_BI]) {
    routes.push(ContactRoutes);
  }
  if (
    permission[MODULES.CAMPAIGN_STATS] ||
    permission[MODULES.ABM_STATS] ||
    permission[MODULES.REPORTS_SETUP]
    // RETIRED (2026-10-14): Attribution module retired — not granted, hidden for everyone
    // || permission[MODULES.ATTRIBUTION]
  ) {
    routes.push(ReportRoutes(permission, isAdmin));
  }
  if (
    permission[MODULES.AGENCY_USERS] ||
    permission[MODULES.ADVERTISER_USERS]
  ) {
    routes.push(UserRoutes(permission, isAdmin));
  }
  if (permission[MODULES.TARGETING_EXPRESSION_PRESET]) {
    routes.push(PresetRoutes(permission, false));
    routes.push(CustomListRoutes);
  }

  routes.push(EmailOutreachRoutes(permission)); // add permission check

  return routes;
}

export const ADMIN_ROUTES = [
  AdvertiserRoutes,
  AudienceRoutes({}, true),
  SetupRoutes,
  AssetRoutes,
  LeadRoutes,
  UtmRoutes,
  PixelRoutes,
  ConversionRoutes,
  LinkedInEngagementRoutes,
];
